import { useContext, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import useViewer from '@hooks/useViewer';
import chatUserEnsure from '@general/api/chatUserEnsure';
import { MessagingTokenContext } from '../../icsc-app/js/context/MessagingTokenProvider';
import { ConversationsContext } from '../../icsc-app/js/context/ConversationsProvider';
import { ConversationsClientContext } from '../../icsc-app/js/context/ConversationsClientProvider';

const useSendMessageOutsideInbox = () => {
    const queryClient = useQueryClient();
    const [queue, setQueue] = useState();
    const [error, setError] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const { viewer } = useViewer();
    const { getMessagingToken, error: errorToken } = useContext(MessagingTokenContext);
    const { conversationsClient, isReady: isClientReady } = useContext(ConversationsClientContext);
    const { isReady: isConversationsReady, conversations, sendMessageToConversation } = useContext(
        ConversationsContext
    );

    useEffect(() => {
        if (errorToken) {
            setError(errorToken);
        }
    }, [errorToken]);

    useEffect(() => {
        (async () => {
            if (!viewer || !queue || !conversationsClient || !isClientReady || !isConversationsReady) return;

            const createConversation = async toID => {
                let newConvo;
                try {
                    newConvo = await conversationsClient.createConversation();
                    await chatUserEnsure(toID);
                    await newConvo.add(viewer.id);
                    await newConvo.add(toID);
                    return newConvo;
                } catch (e) {
                    console.log('createConversation', e);
                    if (newConvo) {
                        await newConvo.leave();
                        await newConvo.delete();
                    }
                    throw new Error(`Cant' create conversation!`);
                }
            };

            const findExistingConversation = async toID => {
                try {
                    let convoExists;
                    for (const convo of conversations) {
                        const participants = await convo.getParticipants();
                        const participantIsRecipient = participants.find(p => p.state.identity === toID);
                        if (participantIsRecipient) {
                            convoExists = convo;
                            break;
                        }
                    }
                    console.log('finding existing convo', { convoExists });
                    return convoExists;
                } catch (e) {
                    console.log('findExistingConversation', e);
                    throw new Error(`Error finding existing conversation!`);
                }
            };

            const getConversation = async toID => {
                // check if conversation already exists, if not create it
                try {
                    const existingConvo = await findExistingConversation(toID);
                    if (existingConvo) {
                        return existingConvo;
                    }
                    const newConversation = await createConversation(toID);
                    return newConversation;
                } catch (e) {
                    console.log('getConversation', e);
                    throw new Error(`Error getting conversation!`);
                }
            };

            console.log('QUEUED');
            // reset it first because it was running twice
            const messageData = { ...queue };

            setQueue(null);

            try {
                const conversation = await getConversation(messageData.recipientID);
                await sendMessageToConversation({
                    conversation,
                    messageText: messageData.messageText,
                    messageAttributes: messageData.messageAttributes,
                });
                setIsLoading(false);
                setIsSuccess(true);
            } catch (e) {
                setError(e);
            }
        })();
    }, [
        queue,
        conversationsClient,
        sendMessageToConversation,
        conversations,
        viewer,
        isClientReady,
        isConversationsReady,
    ]);

    const sendMessage = async ({ messageText, messageAttributes, recipientID }) => {
        setIsLoading(true);
        setIsSuccess(false);
        setError();
        if (!conversationsClient) {
            console.log('INVAIDATING');
            console.log(viewer.id);
            await getMessagingToken();
            queryClient.invalidateQueries(['chat-user-exists', viewer.id]);
        }
        setQueue({ messageText, messageAttributes, recipientID });
    };

    return { sendMessage, error, isLoading, isSuccess };
};

useSendMessageOutsideInbox.propTypes = {
    // prop: PropTypes.
};

export default useSendMessageOutsideInbox;
